/** Staged-artifact lifecycle for the Add New Product draft. */
import fetchWithAuth from "@/api/secureFetch";
import type { ContextMeshRefreshNotice } from "@/api/integrations/links";

const AGENT_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

export interface StagedCleanupRequest {
    processJobIds?: string[];
    repoIds?: number[];
    fileIds?: number[];
}

export interface StagedAttachSources {
    [source: string]: {
        processJobId?: string;
        repoIds?: number[];
        fileIds?: number[];
        selection?: Array<{ id: string; key?: string; name: string }>;
    };
}

async function expectOk(response: Response, what: string) {
    if (!response.ok) {
        let detail = "";
        try {
            const body = await response.json();
            if (body && typeof body.error === "string") detail = body.error;
        } catch { /* body was not JSON */ }
        throw new Error(detail || `${what} failed: ${response.status}`);
    }
    return response.json();
}

export async function cleanupStagedArtifacts(req: StagedCleanupRequest, token: string): Promise<{ success: boolean }> {
    const response = await fetchWithAuth(`${AGENT_BASE_URL}/api/v1/product-drafts/staged/cleanup`, token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(req)
    });
    return expectOk(response, "Staged cleanup");
}

export async function attachStagedArtifacts(
    projectId: number,
    sources: StagedAttachSources,
    token: string,
    removeLinkIds: number[] = []
): Promise<{ success: boolean; stories_linked?: number } & ContextMeshRefreshNotice> {
    const response = await fetchWithAuth(`${AGENT_BASE_URL}/api/v1/product-drafts/staged/attach`, token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectId, sources, removeLinkIds })
    });
    return expectOk(response, "Staged attach");
}

export async function attachTrackerBatchToProjects(
    processJobId: string, projectIds: number[], token: string
): Promise<{
    success: boolean;
    stories_linked: number;
    stories_per_product: number;
    products_attached: number;
} & ContextMeshRefreshNotice> {
    const response = await fetchWithAuth(`${AGENT_BASE_URL}/api/v1/product-drafts/staged/attach-tracker-batch`, token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ processJobId, projectIds })
    });
    return expectOk(response, "Tracker batch attach");
}
